/**
 * The owner's admin page (/admin): accounts and plans, invite requests, how matching is going, and the feedback
 * members leave. Every call goes to the owner's routes, which the server opens only to the accounts in
 * HUB_ADMIN_EMAILS; anyone else gets a 403 and this page says so. No admin token is ever held in the browser.
 */
import { useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Handshake, Mail, MessageSquare, ShieldCheck, ThumbsDown, ThumbsUp, Users } from "lucide-react";
import { DISMISS_REASON_LABELS, FEEDBACK_PLATFORM_LABELS, MATCH_STAGES, type AdminAccount, type AdminFeedbackResponse, type AdminInviteRequest, type AdminMatch, type MatchStage } from "@hub/shared";
import { Button, EmptyState, Input, Modal, Pill, Select, Spinner, Tabs, formatDate, relativeTime, toast, type Tone } from "@/ui";
import { ApiError, api } from "@/api/client";
import { Page } from "@/app/layout/Page";
import s from "./admin.module.css";

type Tab = "accounts" | "invites" | "matches" | "feedback";
const TABS: { id: Tab; label: ReactNode }[] = [
  { id: "accounts", label: <span className="u-row" style={{ gap: 6 }}><Users size={14} /> Accounts</span> },
  { id: "invites", label: <span className="u-row" style={{ gap: 6 }}><Mail size={14} /> Invite requests</span> },
  { id: "matches", label: <span className="u-row" style={{ gap: 6 }}><Handshake size={14} /> Matches</span> },
  { id: "feedback", label: <span className="u-row" style={{ gap: 6 }}><MessageSquare size={14} /> Feedback</span> },
];

/** The plan names in docs/BILLING.md; the server still stores the old codes. */
export const PLAN_LABELS: Record<string, string> = { personal: "Guest", pro: "Business", teams: "Team", enterprise: "Enterprise" };
const PLANS = Object.keys(PLAN_LABELS);

export const STAGE_LABELS: Record<MatchStage, string> = { found: "Found", shared: "Shared", accepted: "Accepted", introduced: "Introduced", done: "Done", dismissed: "Dismissed", expired: "Expired" };
const STAGE_TONES: Record<MatchStage, Tone> = { found: "neutral", shared: "info", accepted: "accent", introduced: "purple", done: "success", dismissed: "danger", expired: "neutral" };

const ADMIN_KEY = ["admin"] as const;

export function AdminPage() {
  const params = useParams();
  const navigate = useNavigate();
  const tab: Tab = TABS.some((t) => t.id === params.tab) ? (params.tab as Tab) : "accounts";
  // One cheap call decides whether the page opens at all.
  const gate = useQuery({ queryKey: [...ADMIN_KEY, "accounts"], queryFn: () => api.admin.accounts(), retry: false });
  const refused = gate.error instanceof ApiError && (gate.error.status === 403 || gate.error.status === 404 || gate.error.status === 401);

  return (
    <Page title="Admin" narrow={false}>
      {gate.isLoading && <div className={s.center}><Spinner /></div>}
      {refused && (
        <EmptyState icon={<ShieldCheck size={26} className="u-muted" />} title="This page is for Hub's owner">
          Your account is not on the server's list of admins (HUB_ADMIN_EMAILS).
        </EmptyState>
      )}
      {gate.error && !refused && <EmptyState title="The admin page could not load">{gate.error instanceof Error ? gate.error.message : String(gate.error)}</EmptyState>}
      {gate.isSuccess && (
        <div className={s.wrap}>
          <Tabs tabs={TABS} value={tab} onChange={(id) => navigate(`/admin/${id}`)} />
          <div className={s.tabBody}>
            {tab === "accounts" && <AccountsTab />}
            {tab === "invites" && <InvitesTab />}
            {tab === "matches" && <MatchesTab />}
            {tab === "feedback" && <FeedbackTab />}
          </div>
        </div>
      )}
    </Page>
  );
}

const errorText = (e: unknown) => (e instanceof Error ? e.message : "Something went wrong");

// Accounts --------------------------------------------------------------------------------------------------------

interface PendingChange {
  account: AdminAccount;
  change: { planType: string } | { suspended: boolean };
}

function AccountsTab() {
  const qc = useQueryClient();
  const accounts = useQuery({ queryKey: [...ADMIN_KEY, "accounts"], queryFn: () => api.admin.accounts() });
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState<PendingChange | null>(null);
  const update = useMutation({
    mutationFn: ({ account, change }: PendingChange) => api.admin.updateAccount(account.id, change),
    onSuccess: (res, { change }) => {
      qc.invalidateQueries({ queryKey: [...ADMIN_KEY, "accounts"] });
      setPending(null);
      if ("planType" in change) toast(`${res.user.email} is now on ${PLAN_LABELS[res.user.planType] ?? res.user.planType}.`, "success");
      else if (change.suspended) toast(`${res.user.email} suspended. Signed out of ${res.sessionsRevoked} session${res.sessionsRevoked === 1 ? "" : "s"}.`, "success");
      else toast(`${res.user.email} restored. They sign in again on each device.`, "success");
    },
    onError: (e) => toast(errorText(e), "danger"),
  });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = accounts.data?.users ?? [];
    return q ? all.filter((a) => a.email.toLowerCase().includes(q) || (a.name ?? "").toLowerCase().includes(q) || (PLAN_LABELS[a.planType] ?? a.planType).toLowerCase() === q) : all;
  }, [accounts.data, query]);
  const total = accounts.data?.users.length ?? 0;

  return (
    <div className="u-col" style={{ gap: 12 }}>
      <div className={s.toolbar}>
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by email, name or plan" aria-label="Search accounts" className={s.search} />
        <span className="u-small u-muted">{rows.length === total ? `${total} accounts` : `${rows.length} of ${total} accounts`}</span>
      </div>
      <div className={s.tableWrap}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>Account</th>
              <th>Plan</th>
              <th className={s.num}>People</th>
              <th className={s.num}>Devices</th>
              <th>Joined</th>
              <th>Last seen</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} data-suspended={a.suspended ? "true" : undefined}>
                <td>
                  <div className={s.strong}>{a.name ?? a.email}</div>
                  {a.name && <div className="u-small u-muted">{a.email}</div>}
                  {a.suspended && <Pill tone="danger" title={a.suspendedAt ? `Since ${formatDate(a.suspendedAt)}` : undefined}>Suspended</Pill>}
                </td>
                <td>
                  <Select value={a.planType} aria-label={`Plan for ${a.email}`} onChange={(e) => e.target.value !== a.planType && setPending({ account: a, change: { planType: e.target.value } })}>
                    {PLANS.map((p) => (
                      <option key={p} value={p}>
                        {PLAN_LABELS[p]}
                      </option>
                    ))}
                  </Select>
                </td>
                <td className={s.num}>{a.people.toLocaleString()}</td>
                <td className={s.num}>{a.devices}</td>
                <td title={a.created}>{formatDate(a.created)}</td>
                <td title={a.lastSeenAt ?? undefined}>{a.lastSeenAt ? relativeTime(a.lastSeenAt) : <span className="u-muted">never</span>}</td>
                <td className={s.actions}>
                  {a.suspended ? (
                    <Button size="sm" variant="outline" onClick={() => setPending({ account: a, change: { suspended: false } })}>
                      Restore
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => setPending({ account: a, change: { suspended: true } })}>
                      Suspend
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {accounts.isSuccess && rows.length === 0 && <div className={s.empty}>No account matches "{query}".</div>}
      </div>
      <ConfirmChange pending={pending} busy={update.isPending} onCancel={() => setPending(null)} onConfirm={() => pending && update.mutate(pending)} />
    </div>
  );
}

function ConfirmChange({ pending, busy, onCancel, onConfirm }: { pending: PendingChange | null; busy: boolean; onCancel: () => void; onConfirm: () => void }) {
  if (!pending) return null;
  const { account, change } = pending;
  const who = account.name ? `${account.name} (${account.email})` : account.email;
  let title: string;
  let body: string;
  let action: string;
  if ("planType" in change) {
    title = "Change plan";
    body = `Move ${who} from ${PLAN_LABELS[account.planType] ?? account.planType} to ${PLAN_LABELS[change.planType] ?? change.planType}. Their apps learn the plan on their next refresh.`;
    action = `Move to ${PLAN_LABELS[change.planType] ?? change.planType}`;
  } else if (change.suspended) {
    title = "Suspend account";
    body = `Suspend ${who}: they are signed out on every device, cannot sign in and their API keys stop working. Nothing is deleted. Suspending is for abuse or a broken agreement; someone who stops paying becomes a guest instead.`;
    action = "Suspend";
  } else {
    title = "Restore account";
    body = `Restore ${who}. They can sign in again on each device; API keys they had stay revoked.`;
    action = "Restore";
  }
  return (
    <Modal
      open
      onClose={onCancel}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant={"suspended" in change && change.suspended ? "danger" : "primary"} loading={busy} onClick={onConfirm} data-testid="admin-confirm">
            {action}
          </Button>
        </>
      }
    >
      <p style={{ margin: 0 }}>{body}</p>
    </Modal>
  );
}

// Invite requests -------------------------------------------------------------------------------------------------

type InviteFilter = AdminInviteRequest["status"] | "all";
const INVITE_FILTERS: { id: InviteFilter; label: string }[] = [
  { id: "new", label: "New" },
  { id: "waitlist", label: "Waitlist" },
  { id: "invited", label: "Invited" },
  { id: "declined", label: "Declined" },
  { id: "all", label: "All" },
];
const INVITE_TONES: Record<AdminInviteRequest["status"], Tone> = { new: "warning", waitlist: "info", invited: "success", declined: "neutral" };
const INVITE_LABELS: Record<AdminInviteRequest["status"], string> = { new: "New", waitlist: "Waitlist", invited: "Invited", declined: "Declined" };

/** The shown requests as a CSV file, for writing to the list from a mail tool. */
function downloadCsv(rows: AdminInviteRequest[], filter: InviteFilter) {
  const cell = (v: string | null) => (v == null ? "" : /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const lines = [["email", "name", "status", "asked", "uses", "does", "would use it for", "source", "note"].join(",")];
  for (const r of rows) lines.push([r.email, r.name, r.status, r.createdAt.slice(0, 10), r.platform, r.about, r.uses, r.source, r.note].map(cell).join(","));
  const url = URL.createObjectURL(new Blob([lines.join("\n") + "\n"], { type: "text/csv" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: `hub-invite-requests-${filter}-${new Date().toISOString().slice(0, 10)}.csv` });
  a.click();
  URL.revokeObjectURL(url);
}
const PLATFORM_LABELS: Record<string, string> = { mac: "a Mac", iphone: "an iPhone", both: "a Mac and an iPhone", windows: "Windows", android: "Android", other: "something else" };

function InvitesTab() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<InviteFilter>("new");
  const requests = useQuery({ queryKey: [...ADMIN_KEY, "invites", filter], queryFn: () => api.admin.inviteRequests(filter === "all" ? undefined : filter) });
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const invalidate = () => qc.invalidateQueries({ queryKey: [...ADMIN_KEY, "invites"] });
  const triage = useMutation({
    mutationFn: ({ id, status }: { id: string; status: AdminInviteRequest["status"] }) => api.admin.updateInviteRequest(id, { status }),
    onSuccess: (res, { status }) => {
      invalidate();
      if (status === "invited") toast(res.emailed ? `Invited ${res.request.email}. They were emailed where to sign in.` : `Invited ${res.request.email}. No email went out (they may already have an account).`, "success");
      else if (status === "waitlist") toast(`${res.request.email} is on the waitlist. No email is sent.`, "neutral");
      else toast(`Declined ${res.request.email}. No email is sent.`, "neutral");
    },
    onError: (e) => toast(errorText(e), "danger"),
  });
  const [editing, setEditing] = useState<{ id: string; note: string } | null>(null);
  const saveNote = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => api.admin.updateInviteRequest(id, { note: note.trim() || null }),
    onSuccess: () => {
      invalidate();
      setEditing(null);
    },
    onError: (e) => toast(errorText(e), "danger"),
  });
  const invite = useMutation({
    mutationFn: () => api.admin.invite(email.trim(), note.trim() || null),
    onSuccess: (res) => {
      invalidate();
      setEmail("");
      setNote("");
      toast(res.emailed ? `Invited ${res.request.email} and emailed them.` : `Invited ${res.request.email}. No email went out.`, "success");
    },
    onError: (e) => toast(errorText(e), "danger"),
  });

  return (
    <div className="u-col" style={{ gap: 16 }}>
      <form
        className={s.inviteForm}
        onSubmit={(e) => {
          e.preventDefault();
          if (email.trim()) invite.mutate();
        }}
      >
        <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Invite someone: their email" type="email" aria-label="Email to invite" className={s.search} />
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note to yourself (optional)" aria-label="Note" className={s.search} />
        <Button type="submit" variant="primary" disabled={!email.trim()} loading={invite.isPending}>
          Invite
        </Button>
      </form>
      <div className={s.chips}>
        {INVITE_FILTERS.map((f) => (
          <button key={f.id} type="button" className={s.chip} data-active={filter === f.id ? "true" : undefined} onClick={() => setFilter(f.id)}>
            {f.label}
          </button>
        ))}
        <span className="u-grow" />
        <Button size="sm" variant="ghost" disabled={!requests.data?.requests.length} onClick={() => requests.data && downloadCsv(requests.data.requests, filter)}>
          <Download size={14} /> Export CSV
        </Button>
      </div>
      {requests.isLoading && <div className={s.center}><Spinner /></div>}
      {requests.isSuccess && requests.data.requests.length === 0 && <div className={s.empty}>{filter === "new" ? "No requests are waiting on you." : "No requests here."}</div>}
      <div className="u-col" style={{ gap: 8 }}>
        {requests.data?.requests.map((r) => (
          <div key={r.id} className={s.card}>
            <div className="u-row" style={{ flexWrap: "wrap" }}>
              <span className={s.strong}>{r.name ? `${r.name}, ${r.email}` : r.email}</span>
              <Pill tone={INVITE_TONES[r.status]}>{INVITE_LABELS[r.status]}</Pill>
              <span className="u-small u-muted" title={r.createdAt}>
                {relativeTime(r.createdAt)} from {r.source ?? "the website"}
                {r.platform ? `, uses ${PLATFORM_LABELS[r.platform] ?? r.platform}` : ""}
              </span>
              <span className="u-grow" />
              {r.status !== "invited" && (
                <Button size="sm" variant="primary" loading={triage.isPending && triage.variables?.id === r.id && triage.variables.status === "invited"} onClick={() => triage.mutate({ id: r.id, status: "invited" })}>
                  Invite
                </Button>
              )}
              {r.status === "new" && (
                <Button size="sm" variant="ghost" onClick={() => triage.mutate({ id: r.id, status: "waitlist" })}>
                  Waitlist
                </Button>
              )}
              {(r.status === "new" || r.status === "waitlist") && (
                <Button size="sm" variant="ghost" onClick={() => triage.mutate({ id: r.id, status: "declined" })}>
                  Decline
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setEditing({ id: r.id, note: r.note ?? "" })}>
                Note
              </Button>
            </div>
            {r.about && <div className="u-small"><span className="u-muted">Does: </span>{r.about}</div>}
            {r.uses && <div className="u-small"><span className="u-muted">Would use it for: </span>{r.uses}</div>}
            {editing?.id === r.id ? (
              <form
                className="u-row"
                onSubmit={(e) => {
                  e.preventDefault();
                  saveNote.mutate(editing);
                }}
              >
                <Input autoFocus value={editing.note} onChange={(e) => setEditing({ id: r.id, note: e.target.value })} placeholder="Note to yourself" aria-label="Note" className="u-grow" />
                <Button size="sm" type="submit" variant="primary" loading={saveNote.isPending}>Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
              </form>
            ) : (
              r.note && <div className="u-small"><span className="u-muted">Note: </span>{r.note}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Matches ---------------------------------------------------------------------------------------------------------

function MatchesTab() {
  const report = useQuery({ queryKey: [...ADMIN_KEY, "matches"], queryFn: () => api.admin.matches() });
  const [stage, setStage] = useState<MatchStage | "all" | "stalled">("all");
  if (report.isLoading) return <div className={s.center}><Spinner /></div>;
  if (report.error) return <div className={s.empty}>{errorText(report.error)}</div>;
  const data = report.data!;
  const totalStalled = data.stalled.reduce((n, r) => n + r.count, 0);
  const rows = data.matches.filter((m) => (stage === "all" ? true : stage === "stalled" ? m.stalled !== null : m.stage === stage));

  return (
    <div className="u-col" style={{ gap: 16 }}>
      <div className={s.tiles}>
        {MATCH_STAGES.map((st) => (
          <button key={st} type="button" className={s.tile} data-active={stage === st ? "true" : undefined} onClick={() => setStage(stage === st ? "all" : st)}>
            <span className={s.tileValue}>{data.counts[st]}</span>
            <span className={s.tileLabel}>{STAGE_LABELS[st]}</span>
          </button>
        ))}
      </div>

      <section className="u-col" style={{ gap: 6 }}>
        <h2 className={s.h2}>Where matches stall</h2>
        <div className={s.stalls}>
          {data.stalled.map((r) => (
            <div key={r.rule} className={s.stallRow} data-zero={r.count === 0 ? "true" : undefined}>
              <span>{r.rule}</span>
              <span className={s.strong}>{r.count}</span>
            </div>
          ))}
        </div>
        {totalStalled > 0 && (
          <div>
            <Button size="sm" variant={stage === "stalled" ? "primary" : "outline"} onClick={() => setStage(stage === "stalled" ? "all" : "stalled")}>
              {stage === "stalled" ? "Show every match" : `Show the ${totalStalled} stalled`}
            </Button>
          </div>
        )}
      </section>

      <section className="u-col" style={{ gap: 6 }}>
        <h2 className={s.h2}>
          {stage === "all" ? "Every match" : stage === "stalled" ? "Stalled matches" : `${STAGE_LABELS[stage]} matches`} <span className="u-muted">({rows.length})</span>
        </h2>
        {rows.length === 0 ? (
          <div className={s.empty}>No matches here yet.</div>
        ) : (
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Needs</th>
                  <th>Can help</th>
                  <th className={s.num}>Score</th>
                  <th>Stage</th>
                  <th>Last change</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <MatchRow key={m.id} m={m} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="u-col" style={{ gap: 6 }}>
        <h2 className={s.h2}>
          Completion ratings {data.averageRating !== null && <span className="u-muted">(average {data.averageRating.toFixed(1)} of 5)</span>}
        </h2>
        {data.ratings.length === 0 ? (
          <div className={s.empty}>No introduction has been marked done yet.</div>
        ) : (
          <div className="u-col" style={{ gap: 8 }}>
            {data.ratings.map((r, i) => (
              <div key={`${r.matchId}-${i}`} className={s.card}>
                <div className="u-row" style={{ flexWrap: "wrap" }}>
                  <span className={s.strong}>{r.rating ? `${r.rating} of 5` : "No rating"}</span>
                  <span className="u-small">{r.need}{r.offer ? ` with ${r.offer}` : ""}</span>
                  <span className="u-grow" />
                  <span className="u-small u-muted" title={r.created}>{r.by}, {relativeTime(r.created)}</span>
                </div>
                {r.note && <div className="u-small">{r.note}</div>}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function MatchRow({ m }: { m: AdminMatch }) {
  return (
    <tr>
      <td>
        <div className={s.strong}>{m.needer.name}</div>
        <div className="u-small">{m.need}</div>
        <div className="u-small u-muted">{m.needer.memberName === m.needer.name ? "Member" : `In ${m.needer.memberName}'s people`}</div>
      </td>
      <td>
        <div className={s.strong}>{m.helper.name}</div>
        <div className="u-small">{m.offer}</div>
        <div className="u-small u-muted">{m.helper.memberName === m.helper.name ? "Member" : `In ${m.helper.memberName}'s people`}</div>
      </td>
      <td className={s.num}>{Math.round(m.score * 100)}%</td>
      <td>
        <Pill tone={STAGE_TONES[m.stage]} title={m.status.replace(/_/g, " ")}>
          {STAGE_LABELS[m.stage]}
        </Pill>
        {m.dismissReason && <div className="u-small u-muted">{DISMISS_REASON_LABELS[m.dismissReason]}</div>}
        {m.stalled && <div className={s.stalled}>{m.stalled}</div>}
      </td>
      <td title={m.lastChangeAt}>
        <div className="u-small">{relativeTime(m.lastChangeAt)}</div>
        {m.lastChangeBy && <div className="u-small u-muted">by {m.lastChangeBy}</div>}
      </td>
    </tr>
  );
}

// Feedback --------------------------------------------------------------------------------------------------------

function FeedbackMessages({ messages }: { messages: AdminFeedbackResponse["messages"] }) {
  const qc = useQueryClient();
  const [waitingOnly, setWaitingOnly] = useState(true);
  const mark = useMutation({
    mutationFn: ({ id, handled }: { id: string; handled: boolean }) => api.admin.markFeedbackHandled(id, handled),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...ADMIN_KEY, "feedback"] }),
    onError: (e) => toast(errorText(e), "danger"),
  });
  const items = waitingOnly ? messages.items.filter((m) => m.handledAt === null) : messages.items;

  return (
    <section className="u-col" style={{ gap: 8 }}>
      <div className="u-row" style={{ gap: 16, flexWrap: "wrap" }}>
        <h2 className={s.h2}>
          Messages <span className="u-muted">({messages.open} waiting)</span>
        </h2>
        <span className="u-grow" />
        <div className={s.chips}>
          <button type="button" className={s.chip} data-active={waitingOnly ? "true" : undefined} onClick={() => setWaitingOnly(true)}>Waiting</button>
          <button type="button" className={s.chip} data-active={!waitingOnly ? "true" : undefined} onClick={() => setWaitingOnly(false)}>All</button>
        </div>
      </div>
      {items.length === 0 && <div className={s.empty}>{waitingOnly ? "No feedback is waiting on you." : "No one has sent feedback yet."}</div>}
      {items.map((m) => (
        <div key={m.id} className={s.card} data-handled={m.handledAt ? "true" : undefined}>
          <div className={s.message}>{m.message}</div>
          <div className="u-row" style={{ flexWrap: "wrap" }}>
            <span className="u-small u-muted" title={m.created}>
              {m.userName ? `${m.userName} (${m.userEmail})` : m.userEmail} on {FEEDBACK_PLATFORM_LABELS[m.platform] ?? m.platform}
              {m.appVersion ? ` ${m.appVersion}` : ""}
              {m.context ? `, from ${m.context}` : ""}, {relativeTime(m.created)}
            </span>
            <span className="u-grow" />
            {m.handledAt ? (
              <>
                <Pill tone="success" title={`Handled ${formatDate(m.handledAt)}`}>Handled</Pill>
                <Button size="sm" variant="ghost" loading={mark.isPending && mark.variables?.id === m.id} onClick={() => mark.mutate({ id: m.id, handled: false })}>
                  Reopen
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" loading={mark.isPending && mark.variables?.id === m.id} onClick={() => mark.mutate({ id: m.id, handled: true })}>
                Mark handled
              </Button>
            )}
          </div>
        </div>
      ))}
    </section>
  );
}

function FeedbackTab() {
  const report = useQuery({ queryKey: [...ADMIN_KEY, "feedback"], queryFn: () => api.admin.feedback() });
  const [downOnly, setDownOnly] = useState(true);
  if (report.isLoading) return <div className={s.center}><Spinner /></div>;
  if (report.error) return <div className={s.empty}>{errorText(report.error)}</div>;
  const { messages, ask, dismissals } = report.data!;
  const askItems = downOnly ? ask.items.filter((f) => f.rating === "down") : ask.items;
  const dismissedTotal = dismissals.byReason.reduce((n, r) => n + r.count, 0);

  return (
    <div className="u-col" style={{ gap: 24 }}>
      {/* An older server answers without messages. */}
      {messages && <FeedbackMessages messages={messages} />}
      <div className={s.feedbackGrid}>
        <section className="u-col" style={{ gap: 8 }}>
          <h2 className={s.h2}>Ask answers</h2>
          <div className="u-row" style={{ gap: 16, flexWrap: "wrap" }}>
            <span className="u-row" style={{ gap: 6 }}><ThumbsUp size={15} /> {ask.up} helpful</span>
            <span className="u-row" style={{ gap: 6 }}><ThumbsDown size={15} /> {ask.down} not helpful</span>
            <span className="u-grow" />
            <div className={s.chips}>
              <button type="button" className={s.chip} data-active={downOnly ? "true" : undefined} onClick={() => setDownOnly(true)}>Not helpful</button>
              <button type="button" className={s.chip} data-active={!downOnly ? "true" : undefined} onClick={() => setDownOnly(false)}>All</button>
            </div>
          </div>
          {askItems.length === 0 && <div className={s.empty}>{downOnly ? "No thumbs-down yet." : "No ratings yet."}</div>}
          {askItems.map((f) => (
            <div key={f.id} className={s.card}>
              <div className="u-row" style={{ alignItems: "flex-start" }}>
                <span className={s.thumb} data-rating={f.rating} aria-label={f.rating === "up" ? "Helpful" : "Not helpful"}>
                  {f.rating === "up" ? <ThumbsUp size={14} /> : <ThumbsDown size={14} />}
                </span>
                <div className="u-grow u-col" style={{ gap: 4, minWidth: 0 }}>
                  <div className={s.strong}>{f.question}</div>
                  {f.answerExcerpt && <div className={`u-small u-muted ${s.excerpt}`}>{f.answerExcerpt}</div>}
                  {f.comment && <div className="u-small">"{f.comment}"</div>}
                  <div className="u-small u-muted" title={f.created}>
                    {f.userEmail}
                    {f.platform ? ` on ${f.platform === "ios" ? "iPhone" : f.platform === "mac" ? "Mac" : "the web"}` : ""}, {relativeTime(f.created)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </section>
  
        <section className="u-col" style={{ gap: 8 }}>
          <h2 className={s.h2}>Why matches are dismissed</h2>
          {dismissedTotal === 0 ? (
            <div className={s.empty}>No match has been dismissed since reasons were recorded.</div>
          ) : (
            <div className={s.bars}>
              {dismissals.byReason.map((r) => (
                <div key={r.reason ?? "none"} className={s.barRow}>
                  <span className={s.barLabel}>{r.reason ? DISMISS_REASON_LABELS[r.reason] : "No reason given"}</span>
                  <span className={s.barTrack}>
                    <span className={s.barFill} style={{ width: `${Math.max(4, (r.count / dismissedTotal) * 100)}%` }} />
                  </span>
                  <span className={s.strong}>{r.count}</span>
                </div>
              ))}
            </div>
          )}
          {dismissals.items.map((d) => (
            <div key={d.id} className={s.card}>
              <div className="u-row" style={{ flexWrap: "wrap" }}>
                <Pill tone={d.reason ? "neutral" : "warning"}>{d.reason ? DISMISS_REASON_LABELS[d.reason] : "No reason given"}</Pill>
                <span className="u-small">{d.need} with {d.offer}</span>
              </div>
              {d.comment && <div className="u-small">"{d.comment}"</div>}
              <div className="u-small u-muted" title={d.created}>{d.by}, {relativeTime(d.created)}</div>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
